import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SlateLogo from '../components/SlateLogo';
import LoginDialog from '../components/LoginDialog';
import { AinaaBadge } from '../components/AinaaBadge';
import {
  ArrowRight, Play, FolderKanban, Users, Bot,
  DollarSign, ClipboardList, ListTodo, FileText, Shield,
  Zap, Search, Film, Palette, Camera, Briefcase,
  Star, Check, ChevronLeft, ChevronRight, Heart, Share2,
  Sun, Moon
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { isMobile, getStoreUrl, isIOS } from '../utils/mobileLink';

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function LandingNav({ onLogin }) {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const handleCTA = () => {
    onLogin();
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/80 backdrop-blur-xl' : ''}`}
      data-testid="landing-nav"
    >
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <SlateLogo size={22} />
          <span className="font-heading text-[15px] font-semibold tracking-tight">Slate</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#people" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How?</a>
          <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Why?</a>
          <a href="#showcase" className="text-sm font-medium text-[hsl(36,80%,52%)] hover:text-[hsl(36,80%,42%)] transition-colors">Crowdfunding</a>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
            data-testid="landing-theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
          </button>
          {user ? (
            <Button asChild size="sm" data-testid="nav-go-to-dashboard"><Link to="/dashboard">Dashboard <ArrowRight className="w-4 h-4 ml-1.5" /></Link></Button>
          ) : (
            <Button size="sm" onClick={handleCTA} data-testid="nav-get-started-btn">Get Started</Button>
          )}
        </div>
      </div>
    </motion.nav>
  );
}

function HeroSection({ onLogin }) {
  const handleCTA = () => { onLogin(); };
  const avatars = [
    'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=80&h=80&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1544168190-79c17527004f?w=80&h=80&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1598411435746-47bfecc3ea98?w=80&h=80&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1656478284701-d8be2891e24a?w=80&h=80&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1613309561345-87a0798e1d06?w=80&h=80&fit=crop&crop=face',
  ];

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center pt-14" data-testid="hero-section">
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-8">
          <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] mt-8">
            Build teams.<br />Run projects.<br />Move faster.
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Slate helps creators, filmmakers, agencies and teams manage projects, find collaborators and execute work with AI.
          </motion.p>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 pt-2">
            <Button size="lg" className="h-12 px-8 text-sm" onClick={handleCTA} data-testid="hero-get-started-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" size="lg" className="h-12 px-8 text-sm gap-2" data-testid="hero-watch-demo-btn">
              <Play className="w-4 h-4" /> Watch Demo
            </Button>
          </motion.div>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 pt-1">
            <div className="flex -space-x-2.5">
              {avatars.map((src) => (
                <img key={src} src={src} alt="" className="w-7 h-7 rounded-full border-2 border-background object-cover" />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Join <span className="text-foreground font-medium">200+</span> creators already on Slate</p>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="relative mt-24 mx-auto max-w-3xl">
          <div className="rounded-xl border border-border bg-card p-1 shadow-xl shadow-foreground/[0.04]">
            <div className="rounded-lg bg-card p-6 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[hsl(36,80%,52%)]" /><span className="text-xs font-medium">Dashboard</span></div>
                <div className="flex gap-1">{[1,2,3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-foreground/10" />)}</div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Active Projects', value: '7' },
                  { label: 'Team Members', value: '24' },
                  { label: 'Tasks Done', value: '156' },
                  { label: 'Budget Used', value: '68%' },
                ].map(s => (
                  <div key={s.label} className="bg-secondary rounded-lg p-3">
                    <p className="text-lg font-heading font-semibold">{s.value}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 bg-secondary/60 rounded-lg p-3 h-20 flex items-end">
                  <div className="flex items-end gap-1 w-full">{[40,65,45,80,55,70,90,60,75,85,50,95].map((h,i) => <div key={`bar-${h}-${i}`} className="flex-1 rounded-sm" style={{height: `${h}%`, backgroundColor: i === 6 || i === 11 ? 'hsl(36,80%,52%)' : 'hsl(0,0%,0%,0.08)'}} />)}</div>
                </div>
                <div className="bg-secondary/60 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">AI Insights</p>
                  <div className="space-y-1.5"><div className="h-1.5 bg-foreground/[0.06] rounded-full w-full" /><div className="h-1.5 bg-[hsl(36,80%,52%)]/20 rounded-full w-3/4" /><div className="h-1.5 bg-foreground/[0.04] rounded-full w-1/2" /></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SocialProof() {
  const faces = [
    'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=60&h=60&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1544168190-79c17527004f?w=60&h=60&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1656478284701-d8be2891e24a?w=60&h=60&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1613309561345-87a0798e1d06?w=60&h=60&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1598411435746-47bfecc3ea98?w=60&h=60&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1521870749737-92928aafcc21?w=60&h=60&fit=crop&crop=face',
  ];

  const logos = [
    { name: 'Dharma Productions', src: '/logos/dharma.webp', cls: 'w-28 h-10' },
    { name: 'Excel Entertainment', src: '/logos/excel.webp', cls: 'w-20 h-10' },
    { name: 'Yash Raj Films', src: '/logos/yrf.svg', cls: 'w-14 h-12' },
    { name: 'Phantom Films', src: '/logos/phantom.svg', cls: 'w-24 h-10' },
    { name: 'TVF', src: '/logos/tvf.svg', cls: 'w-14 h-10' },
    { name: 'Sikhya Entertainment', src: '/logos/sikhya.svg', cls: 'w-24 h-10' },
  ];

  return (
    <section className="py-20" data-testid="social-proof-section">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="flex -space-x-2">
            {faces.map((src) => (
              <img key={src} src={src} alt="" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
            ))}
          </div>
          <div className="w-8 h-8 rounded-full bg-[hsl(36,80%,52%)]/10 border-2 border-background flex items-center justify-center text-[9px] font-semibold text-[hsl(36,80%,52%)]">+200</div>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-10">Trusted by creators, filmmakers and teams</p>
        <div className="flex items-center justify-center gap-10 md:gap-14 flex-wrap">
          {logos.map(l => (
            <div key={l.name} className="flex items-center justify-center opacity-[0.35] hover:opacity-50 transition-opacity" style={{ mixBlendMode: 'multiply' }}>
              <img src={l.src} alt={l.name} className={`${l.cls} object-contain grayscale`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    { icon: FolderKanban, title: 'Projects', desc: 'Create, manage and track projects with timelines, budgets and team assignments.', accent: false },
    { icon: Bot, title: 'Specter\u2122', desc: 'Your AI production assistant. Generate budgets, call sheets, contracts and timelines.', accent: true },
    { icon: Users, title: 'Matchmaking', desc: 'Find the perfect collaborators with AI-powered recommendations from Introw.', accent: true },
    { icon: DollarSign, title: 'Budgets & Invoices', desc: 'Track project finances with detailed budgets, invoices and payment tracking in \u20B9.', accent: false, rupee: true },
    { icon: ClipboardList, title: 'Call Sheets', desc: 'Generate professional call sheets with crew details, locations and schedules.', accent: false },
    { icon: ListTodo, title: 'Tasks & Approvals', desc: 'Manage tasks, assignments and approval workflows across your team.', accent: false },
    { icon: FileText, title: 'Files & Collab', desc: 'Share files, give feedback and collaborate with your team in real time.', accent: false },
    { icon: Shield, title: 'AINAA Trust', desc: 'View trust scores, reliability metrics and verified credentials from AINAA.', accent: true },
  ];

  const capabilities = ['Generate project budgets and timelines', 'Create call sheets and crew lists', 'Summarize project updates', 'Predict bottlenecks and delays', 'Recommend collaborators', 'Draft contracts and invoices'];

  return (
    <section id="features" className="py-24" data-testid="features-section">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-sm text-muted-foreground mb-3">Everything you need</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Projects, people and production</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mt-4 max-w-xl mx-auto">From crew to contracts, Slate handles everything your creative project needs.</motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map(f => (
            <motion.div key={f.title} variants={fadeUp} className="group p-6 rounded-xl border border-border bg-card hover:bg-secondary/40 transition-colors duration-200">
              {f.rupee ? (
                <span className={`inline-block w-5 h-5 mb-4 text-center text-base font-semibold leading-5 ${f.accent ? 'text-[hsl(36,80%,52%)]' : 'text-muted-foreground'}`}>{'\u20B9'}</span>
              ) : (
                <f.icon className={`w-5 h-5 mb-4 ${f.accent ? 'text-[hsl(36,80%,52%)]' : 'text-muted-foreground'}`} />
              )}
              <h3 className="font-heading text-sm font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Specter™ Deep Dive */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center" data-testid="spectre-section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-sm text-[hsl(36,80%,52%)] mb-3">Specter{'\u2122'}</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Your AI production assistant</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground mb-8">Specter{'\u2122'} is deeply integrated across Slate. Not a chatbot in the corner — a contextual AI layer that understands your projects.</motion.p>
            <motion.div variants={stagger} className="space-y-3">
              {capabilities.map(c => (
                <motion.div key={c} variants={fadeUp} className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[hsl(36,80%,52%)]" /><span className="text-sm text-muted-foreground">{c}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center gap-2 mb-2"><Bot className="w-4 h-4 text-[hsl(36,80%,52%)]" /><span className="text-xs font-medium">Specter{'\u2122'}</span></div>
              <div className="bg-secondary rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-3">Budget breakdown for "Echoes of Mumbai"</p>
                {[['Pre-production', '4.2L'], ['Production', '15.8L'], ['Post-production', '3.5L'], ['Marketing', '1.5L']].map(([l, v]) => (
                  <div key={l} className="flex justify-between text-sm py-1"><span className="text-muted-foreground">{l}</span><span className="font-medium">{v}</span></div>
                ))}
                <div className="border-t border-border pt-2 mt-1 flex justify-between text-sm"><span className="font-semibold">Total</span><span className="font-semibold">25.0L</span></div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Ask Specter™ Search Graphic */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 max-w-2xl mx-auto" data-testid="spectre-search-graphic">
          <div className="rounded-xl border border-border bg-card p-8">
            <div className="flex items-center gap-3 mb-6"><Search className="w-5 h-5 text-muted-foreground" /><p className="text-sm text-muted-foreground">Ask Specter{'\u2122'} to find...</p></div>
            <p className="font-heading text-lg font-medium mb-6">"Need a cinematographer for a gritty indie thriller under 20L budget in Mumbai"</p>
            <div className="space-y-2">
              {[
                { name: 'Arjun Mehta', role: 'Cinematographer', ainaa: 'AINAA-PPL-OVHVG', trust: 94, match: 92, avatar: 'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=100&h=100&fit=crop&crop=face' },
                { name: 'Vikram Reddy', role: 'Cinematographer', ainaa: 'AINAA-PPL-B4D54', trust: 96, match: 95, avatar: 'https://images.unsplash.com/photo-1598411435746-47bfecc3ea98?w=100&h=100&fit=crop&crop=face' },
                { name: 'Aisha Khan', role: 'Cinematographer', ainaa: 'AINAA-PPL-GGVSF', trust: 89, match: 84, avatar: 'https://images.unsplash.com/photo-1544168190-79c17527004f?w=100&h=100&fit=crop&crop=face' },
              ].map(p => (
                <div key={p.name} className="flex items-center justify-between p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors">
                  <div className="flex items-center gap-3">
                    <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{p.name}</p>
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[hsl(36,80%,52%)]/15 text-[hsl(36,80%,42%)] text-[9px] font-medium">
                          <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />{p.trust}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono">{p.ainaa} · {p.role}</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-[hsl(36,80%,52%)]">{p.match}% match</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PeopleSection() {
  return (
    <section id="people" className="py-24" data-testid="people-section">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-sm text-[hsl(36,80%,52%)] mb-3">AINAA Network</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Find the right people, faster</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            AINAA is India's first decentralized trust and identity layer for creative professionals.
            Every collaborator on Slate is verified through AINAA — with trust scores, reliability metrics,
            and verified work history — so you know exactly who you're working with.
          </motion.p>
        </motion.div>

        {/* AINAA Trust Architecture Graphic */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border bg-card text-center">
            <Shield className="w-5 h-5 mx-auto mb-3 text-[hsl(36,80%,52%)]" strokeWidth={2} />
            <p className="font-heading text-sm font-semibold mb-1">Trust Score</p>
            <p className="text-xs text-muted-foreground leading-relaxed">Every professional carries a trust score built from verified project completions, on-time delivery, and peer reviews.</p>
          </motion.div>
          <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border bg-card text-center">
            <Check className="w-5 h-5 mx-auto mb-3 text-[hsl(36,80%,52%)]" strokeWidth={2} />
            <p className="font-heading text-sm font-semibold mb-1">Verified Identity</p>
            <p className="text-xs text-muted-foreground leading-relaxed">Real credentials, real work history. No fake profiles. AINAA verifies identities through government ID and professional portfolios.</p>
          </motion.div>
          <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border bg-card text-center">
            <Zap className="w-5 h-5 mx-auto mb-3 text-[hsl(36,80%,52%)]" strokeWidth={2} />
            <p className="font-heading text-sm font-semibold mb-1">AI Matching</p>
            <p className="text-xs text-muted-foreground leading-relaxed">Specter{'\u2122'} uses AINAA data to match you with the right collaborators — factoring in skill, budget, availability, and trust.</p>
          </motion.div>
        </motion.div>

        {/* Testimonials */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-16 max-w-2xl mx-auto space-y-10" data-testid="ainaa-testimonials">
          {[
            { name: 'Rohan Desai', role: 'Director', avatar: 'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=80&h=80&fit=crop&crop=face', quote: 'AINAA changed how I hire. I found a colorist with a 97 trust score in 10 minutes. No more guesswork, no more bad referrals.' },
            { name: 'Priya Sharma', role: 'Producer', avatar: 'https://images.unsplash.com/photo-1544168190-79c17527004f?w=80&h=80&fit=crop&crop=face', quote: 'The verified identity layer is what sealed it for me. Every person I work with on Slate has a real track record. I sleep better at night.' },
            { name: 'Vikram Rathore', role: 'Agency Head', avatar: 'https://images.unsplash.com/photo-1598411435746-47bfecc3ea98?w=80&h=80&fit=crop&crop=face', quote: 'We onboarded 40 freelancers through AINAA in our first month. The trust scores and AI matching save us weeks of vetting time.' },
          ].map(t => (
            <motion.div key={t.name} variants={fadeUp}>
              <p className="text-base text-muted-foreground leading-relaxed italic">"{t.quote}"</p>
              <div className="flex items-center gap-3 mt-3">
                <img src={t.avatar} alt={t.name} className="w-7 h-7 rounded-full object-cover" />
                <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{t.name}</span> · {t.role}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const showcaseFilms = [
  { id: 'sf_001', title: 'Echoes of Mumbai', tagline: 'Three lives. One night. No escape.', genre: 'Thriller / Indie', director: 'Rohan Desai', poster: 'https://images.unsplash.com/photo-1611513182188-a6bd44a54aae?w=800&fit=crop&q=80', goal: 2500000, raised: 1125000, backers: 347, daysLeft: 18, tags: ['Hindi', 'Noir', 'Mumbai'] },
  { id: 'sf_002', title: 'Beyond Borders', tagline: "Nomads don't get lost. They find themselves.", genre: 'Documentary / Travel', director: 'Emma Clarke', poster: 'https://images.unsplash.com/photo-1696332415657-9fbe802a8875?w=800&fit=crop&q=80', goal: 1800000, raised: 720000, backers: 213, daysLeft: 32, tags: ['Travel', 'Central Asia', 'OTT'] },
  { id: 'sf_003', title: 'Maison Noir', tagline: 'Fashion is armour. Style is war.', genre: 'Drama / Fashion', director: 'Amira Hassan', poster: 'https://images.unsplash.com/photo-1614201842267-206a09286c3b?w=800&fit=crop&q=80', goal: 1200000, raised: 960000, backers: 512, daysLeft: 7, tags: ['Fashion', 'Drama', 'Paris'] },
  { id: 'sf_004', title: 'Dil Da Raasta', tagline: 'The road to the heart is never straight.', genre: 'Music / Drama', director: "Tanya D'Souza", poster: 'https://images.unsplash.com/photo-1695192655920-00dfc0c1ea22?w=800&fit=crop&q=80', goal: 350000, raised: 280000, backers: 189, daysLeft: 12, tags: ['Punjabi', 'Music', 'Dance'] },
  { id: 'sf_005', title: 'The Last Gaffer', tagline: 'Some lights never go out.', genre: 'Drama / Indie', director: 'Rohan Desai', poster: 'https://images.unsplash.com/photo-1599971515603-85c2ea0ee19e?w=800&fit=crop&q=80', goal: 800000, raised: 320000, backers: 156, daysLeft: 25, tags: ['Indie', 'Drama', 'Film'] },
  { id: 'sf_006', title: 'Pixel Perfect', tagline: 'Design is never done.', genre: 'Documentary / Tech', director: 'Sara Chen', poster: 'https://images.unsplash.com/photo-1643905962365-efaead921d34?w=800&fit=crop&q=80', goal: 450000, raised: 135000, backers: 98, daysLeft: 40, tags: ['Tech', 'Documentary', 'Design'] },
];

function ShowcaseSection() {
  const [selectedFilm, setSelectedFilm] = useState(null);
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const dragState = useRef({ startX: 0, scrollLeft: 0, dragged: false });
  const CARD_W = 296; // card width + gap

  const [boosts, setBoosts] = useState(() =>
    showcaseFilms.reduce((acc, f) => ({ ...acc, [f.id]: { raised: 0, backers: 0 } }), {})
  );

  // Observe when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Slow funding ticker
  useEffect(() => {
    if (!isVisible) return;
    const tick = () => {
      setBoosts(prev => {
        const next = { ...prev };
        const ids = showcaseFilms.map(f => f.id);
        const count = Math.random() > 0.5 ? 2 : 1;
        for (let i = 0; i < count; i++) {
          const id = ids[Math.floor(Math.random() * ids.length)];
          const film = showcaseFilms.find(f => f.id === id);
          const maxBoostRaised = film.goal * 0.12;
          const maxBoostBackers = Math.round(film.backers * 0.15);
          const curRaised = prev[id].raised;
          const curBackers = prev[id].backers;
          if (curRaised < maxBoostRaised) {
            const bump = Math.round(film.goal * (0.002 + Math.random() * 0.005));
            next[id] = {
              raised: Math.min(curRaised + bump, maxBoostRaised),
              backers: Math.min(curBackers + (Math.random() > 0.4 ? 1 : 0), maxBoostBackers),
            };
          }
        }
        return next;
      });
    };
    const id = setInterval(tick, 2500 + Math.random() * 1500);
    return () => clearInterval(id);
  }, [isVisible]);

  // Track active card index from scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / CARD_W);
      setActiveIdx(Math.min(idx, showcaseFilms.length - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-advance carousel (slow, pauses on hover/drag/selection)
  useEffect(() => {
    if (!isVisible || isPaused || isDragging || selectedFilm) return;
    const id = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxScroll - 10) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: CARD_W, behavior: 'smooth' });
      }
    }, 4000);
    return () => clearInterval(id);
  }, [isVisible, isPaused, isDragging, selectedFilm]);

  // Arrow scroll — snaps by one card
  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = Math.round(el.scrollLeft / CARD_W) + dir;
    el.scrollTo({ left: target * CARD_W, behavior: 'smooth' });
  };

  // Dot click — jump to film
  const jumpTo = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * CARD_W, behavior: 'smooth' });
  };

  // Mouse drag handlers
  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    setIsDragging(true);
    dragState.current = { startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, dragged: false };
    el.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - dragState.current.startX) * 1.5;
    if (Math.abs(walk) > 5) dragState.current.dragged = true;
    el.scrollLeft = dragState.current.scrollLeft - walk;
  };
  const onMouseUp = () => {
    setIsDragging(false);
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
    // Snap to nearest card after drag
    setTimeout(() => {
      const el = scrollRef.current;
      if (el) {
        const nearest = Math.round(el.scrollLeft / CARD_W);
        el.scrollTo({ left: nearest * CARD_W, behavior: 'smooth' });
      }
    }, 50);
  };

  return (
    <section id="showcase" className="py-24" data-testid="showcase-section" ref={sectionRef}>
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[hsl(36,80%,52%)] mb-3">Crowdfunding</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Back the films that matter</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mt-4 max-w-lg mx-auto">Support independent cinema. Fund the stories you want to see made.</motion.p>
        </motion.div>

        {/* Carousel */}
        <div
          className="relative"
          data-testid="showcase-carousel"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => { setIsPaused(false); setIsDragging(false); }}
        >
          <Button
            variant="outline"
            size="icon"
            className="absolute -left-4 top-1/3 -translate-y-1/2 z-10 h-10 w-10 rounded-full border-border bg-background/90 backdrop-blur-sm shadow-lg hover:bg-secondary"
            onClick={() => scrollTo(-1)}
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute -right-4 top-1/3 -translate-y-1/2 z-10 h-10 w-10 rounded-full border-border bg-background/90 backdrop-blur-sm shadow-lg hover:bg-secondary"
            onClick={() => scrollTo(1)}
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </Button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: 'grab', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {showcaseFilms.map((f, i) => {
              const boost = boosts[f.id] || { raised: 0, backers: 0 };
              const liveRaised = f.raised + boost.raised;
              const liveBackers = f.backers + boost.backers;
              const filmPct = Math.round((liveRaised / f.goal) * 100);
              const isSelected = selectedFilm?.id === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => { if (!dragState.current.dragged) setSelectedFilm(isSelected ? null : f); }}
                  className={`flex-shrink-0 w-[280px] cursor-pointer transition-all duration-300 ${isSelected ? 'scale-100 ring-2 ring-[hsl(36,80%,52%)]/40 rounded-xl' : 'hover:scale-[1.02]'}`}
                  style={{ scrollSnapAlign: 'start' }}
                  data-testid={`showcase-card-${f.id}`}
                >
                  <div className="rounded-xl overflow-hidden border border-border/50 bg-card group hover:border-border transition-all">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img src={f.poster} alt={f.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" draggable={false} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <Badge variant="secondary" className="text-[9px] h-4 px-1.5 mb-2 bg-white/20 text-white border-0">{f.genre}</Badge>
                        <h3 className="text-white font-heading text-lg font-semibold leading-tight">{f.title}</h3>
                        <p className="text-white/70 text-[11px] mt-1 italic">{f.tagline}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="w-full h-1.5 rounded-full bg-secondary mb-2">
                        <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all duration-500" style={{ width: `${Math.min(filmPct, 100)}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span className="font-medium text-foreground">{filmPct}% funded</span>
                        <span>{liveBackers} backers</span>
                        <span>{f.daysLeft}d left</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5 mt-4" data-testid="carousel-dots">
            {showcaseFilms.map((f, i) => (
              <button
                key={f.id}
                onClick={() => jumpTo(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? 'w-6 bg-[hsl(36,80%,52%)]' : 'w-1.5 bg-foreground/15 hover:bg-foreground/30'}`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Film detail — only when clicked */}
        <AnimatePresence mode="wait">
          {selectedFilm && (() => {
            const detailBoost = boosts[selectedFilm.id] || { raised: 0, backers: 0 };
            const liveRaised = selectedFilm.raised + detailBoost.raised;
            const liveBackers = selectedFilm.backers + detailBoost.backers;
            const pct = Math.round((liveRaised / selectedFilm.goal) * 100);
            return (
              <motion.div
                key={selectedFilm.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="grid lg:grid-cols-3 gap-8 mt-10"
                data-testid={`showcase-detail-${selectedFilm.id}`}
              >
                <div className="lg:col-span-2">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-heading text-2xl font-semibold">{selectedFilm.title}</h3>
                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{selectedFilm.genre}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground italic mb-3">{selectedFilm.tagline}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-3 mb-4">
                    <span className="flex items-center gap-1"><Film className="w-3.5 h-3.5" strokeWidth={2} /> Dir. {selectedFilm.director}</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {selectedFilm.tags.map(t => <Badge key={t} variant="secondary" className="text-[10px] h-5 px-2 font-normal">{t}</Badge>)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="h-9 gap-1.5" data-testid="back-film-btn"><Heart className="w-3.5 h-3.5" strokeWidth={2} /> Back This Film</Button>
                    <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="share-film-btn"><Share2 className="w-3.5 h-3.5" strokeWidth={2} /> Share</Button>
                  </div>
                </div>
                <div>
                  <div className="p-5 rounded-xl border border-border/50 bg-card">
                    <p className="font-heading text-2xl font-semibold">₹{(liveRaised / 100000).toFixed(1)}L</p>
                    <p className="text-xs text-muted-foreground mb-3">raised of ₹{(selectedFilm.goal / 100000).toFixed(1)}L goal</p>
                    <div className="w-full h-2 rounded-full bg-secondary mb-3">
                      <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><p className="font-heading text-lg font-semibold">{pct}%</p><p className="text-[10px] text-muted-foreground">Funded</p></div>
                      <div><p className="font-heading text-lg font-semibold">{liveBackers}</p><p className="text-[10px] text-muted-foreground">Backers</p></div>
                      <div><p className="font-heading text-lg font-semibold">{selectedFilm.daysLeft}</p><p className="text-[10px] text-muted-foreground">Days Left</p></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    </section>
  );
}


function UseCasesSection() {
  const cases = [
    { icon: Film, title: 'Filmmakers', desc: 'Manage entire productions from pre to post.' },
    { icon: Briefcase, title: 'Agencies', desc: 'Run campaigns and client projects at scale.' },
    { icon: Palette, title: 'Freelancers', desc: 'Organize your work and find new clients.' },
    { icon: Star, title: 'Brands', desc: 'Coordinate content and creative production.' },
    { icon: Zap, title: 'Startups', desc: 'Build products with the right team, fast.' },
    { icon: Camera, title: 'Studios', desc: 'Streamline studio operations and workflows.' },
  ];
  return (
    <section id="usecases" className="py-24" data-testid="usecases-section">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Built for every creative team</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mt-4">From filmmakers to startups, Slate adapts to how you work.</motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cases.map(c => (
            <motion.div key={c.title} variants={fadeUp} className="group p-6 rounded-xl border border-border bg-card hover:bg-secondary/40 transition-colors text-center">
              <c.icon className="w-5 h-5 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-heading text-sm font-semibold mb-1">{c.title}</h3>
              <p className="text-sm text-muted-foreground">{c.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CTASection({ onLogin }) {
  const mobile = isMobile();
  const handleCTA = () => { onLogin(); };
  return (
    <section className="py-24 bg-[hsl(36,60%,97%)] dark:bg-transparent" data-testid="cta-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Ready to build with clarity?</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mb-8 max-w-lg mx-auto">Join creators, filmmakers and teams who use Slate to run production without chaos.</motion.p>
          <motion.div variants={fadeUp} className="flex flex-col items-center gap-3">
            <Button size="lg" className="h-12 px-10 text-sm" onClick={handleCTA} data-testid="cta-get-started-btn">
              Start Building <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {mobile && (
              <div className="flex items-center gap-3 mt-1">
                <a href="https://apps.apple.com/app/slate-project-os/id0000000000" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2" data-testid="cta-app-store-link">App Store</a>
                <span className="text-muted-foreground/30">|</span>
                <a href="https://play.google.com/store/apps/details?id=io.goslate.slate" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2" data-testid="cta-play-store-link">Play Store</a>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-10" data-testid="landing-footer">
      <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <SlateLogo size={16} />
          <span className="font-heading text-sm font-semibold">Slate</span>
          <span className="text-[10px] text-muted-foreground/40 ml-1">app.goslate.io</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <a href="https://ainaa.co" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">AINAA</a>
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <a href="mailto:help@goslate.io" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-border bg-secondary/50 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all" data-testid="footer-contact-btn">help@goslate.io</a>
        </div>
      </div>
    </footer>
  );
}

export { LandingNav, Footer };

function MobileAppBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (!isMobile() || dismissed) return null;
  const ios = isIOS();
  return (
    <motion.div
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      exit={{ y: -60 }}
      className="fixed top-0 left-0 right-0 z-[60] bg-foreground text-background px-4 py-2.5 flex items-center gap-3"
      data-testid="mobile-app-banner"
    >
      <SlateLogo size={20} />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-medium leading-tight">Slate</p>
        <p className="text-[10px] opacity-70">App coming soon on {isIOS() ? 'App Store' : 'Play Store'}</p>
      </div>
      <a
        href={getStoreUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3 py-1 rounded-full bg-background text-foreground text-[11px] font-semibold flex-shrink-0"
        data-testid="mobile-app-banner-open"
      >
        Notify Me
      </a>
      <button
        onClick={() => setDismissed(true)}
        className="text-background/50 hover:text-background text-lg leading-none flex-shrink-0 pl-1"
        data-testid="mobile-app-banner-close"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </motion.div>
  );
}

export default function LandingPage() {
  const [showLogin, setShowLogin] = useState(false);
  const openLogin = () => setShowLogin(true);

  // Auto-open dialog when redirected from /login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === 'true') {
      setShowLogin(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <MobileAppBanner />
      <LandingNav onLogin={openLogin} />
      <HeroSection onLogin={openLogin} />
      <SocialProof />
      <PeopleSection />
      <FeaturesSection />
      <UseCasesSection />
      <ShowcaseSection />
      <CTASection onLogin={openLogin} />
      <Footer />
      <LoginDialog open={showLogin} onOpenChange={setShowLogin} />
    </div>
  );
}
